import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, Button, Typography, Box, CircularProgress, IconButton, LinearProgress } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import {
    listClientsService, createOrgClassSectionsService, getOrgModulesService,
    startClientBulkUploadService, clientBulkUploadStatusService,
} from '../../../services/client.service';
import {
    analyticsLicensesInfoService, analyticsPostUsersCsvAsyncService, analyticsCsvUploadJobStatusService,
} from '../../../services/analyticsLicenses.service';
import { ORG_TYPE_SCHOOL, pickDefaultStudentUserGroup } from '../../../utils/analyticsLicensesExcel';
import { downloadImportTemplate } from '../../../utils/importTemplates';
import EntryModeToggle from './EntryModeToggle.comp';
import {
    UPLOAD_KIND_PARTNER, schoolsImportTemplateKind, parseSchoolsUploadWorkbook,
    schoolRowToPayload, networkRowToPayload, classToShellPayload,
} from '../../../utils/schoolsUploadExcel';

/**
 * Upload schools (and, for a partner, networks; for a network, classes with
 * their students) from the approved Excel template — SNW_template.xlsx /
 * PW_template.xlsx.
 *
 * How the rows are saved:
 *   schools and networks → the bulk upload endpoints
 *                          (/dqin/client/bulk_upload/start + /status): ONE
 *                          request, then a background job on the server that
 *                          creates them a few at a time with the same service
 *                          functions the Add a School / Add a Network forms
 *                          use. This window only polls its progress.
 *   classes with students→ the existing background student upload job — ALL
 *                          the students of a workspace as one job, one block
 *                          per school, worked through 100 rows at a time.
 *   classes, no students → createOrgClassSectionsService (Add a Class), per
 *                          school, in batches.
 *
 * Built for large files (thousands of rows): nothing is sent row by row from
 * the browser, and the lists on screen are capped, with anything that failed
 * listed first.
 *
 * The file is checked before anything is sent — the same rules as the forms —
 * and shown as a preview; nothing is sent while it has a problem. Once started,
 * the upload keeps running if this window is closed: a small bar on the page
 * shows its progress and reopens it. A row that fails does not stop the rest.
 * A row whose contact email is already in the workspace is refused when the
 * file is checked, as Add a School refuses it.
 *
 * Both jobs run on the server. The page only has to stay open so that the
 * classes can be sent once the schools are in.
 */

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';
const TEXT = 'var(--brand-text, #111827)';
const GREEN = '#1B6B55';
const RED = '#C62828';

const STUDENT_UPLOAD_OPTIONS = { ignore_email_verification: false, use_default_password: true };
const JOB_POLL_MS = 2500;
const JOB_STALE_MS = 5 * 60 * 1000;
/** How often the schools / networks job is asked for its progress. */
const BULK_POLL_MS = 2000;
/** Empty classes created per request. */
const CLASS_BATCH = 50;
/** Rows drawn in any one list — the rest are counted. */
const LIST_CAP = 100;
const PREVIEW_ROWS = 50;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const lower = (v) => String(v || '').trim().toLowerCase();
const plural = (n, word) => `${Number(n).toLocaleString()} ${word}${n === 1 ? '' : (word.endsWith('s') ? 'es' : 's')}`;
const errText = (e, fallback) => String(e?.message || fallback).replace(/^NOK:/, '').trim();

/** Every client of the workspace (the list is paged, 100 at a time). */
const loadAllClients = async () => {
    const all = [];
    for (let page = 1; page <= 500; page += 1) {
        const res = await listClientsService({ page, limit: 100, account_filter: 'all' });
        const rows = res?.data?.clients || [];
        all.push(...rows);
        if (rows.length < 100 || all.length >= (res?.data?.total || 0)) break;
    }
    return all;
};

/** What the workspace already has, in the shape the file reader checks against. */
const describeExisting = (clients) => {
    const emails = [], networks = [], schools = [];
    clients.forEach((c) => {
        if (c.contact_email) emails.push(c.contact_email);
        if (c.is_school_network) networks.push({ email: c.contact_email, org_code: c.org_code, org_name: c.org_name });
        else schools.push({ email: c.contact_email, org_code: c.org_code, institute_code: c.institute_code, org_name: c.org_name });
        (c.schools || []).forEach((s) => {
            if (s.contact_email) emails.push(s.contact_email);
            schools.push({ email: s.contact_email, org_code: s.org_code, institute_code: s.institute_code, org_name: s.org_name });
        });
    });
    return { emails, networks, schools };
};

/** Wait for a background student upload job to finish; returns { ok, message, rowErrors }. */
const waitForStudentJob = async (org_code, job_id, onProgress) => {
    let failedPolls = 0;
    for (;;) {
        await sleep(JOB_POLL_MS);
        let job = null;
        try {
            const res = await analyticsCsvUploadJobStatusService({ org_code, job_id });
            job = res === false ? null : res?.data;
            failedPolls = 0;
        } catch (e) {
            failedPolls += 1;
            if (failedPolls >= 4) return { ok: false, rowErrors: [], message: 'Lost track of the upload (connection). It continues on the server — check the classes in a few minutes.' };
            continue;
        }
        if (!job) continue;
        onProgress(Number(job.processed_rows) || 0, Number(job.total_rows) || 0);
        const rowErrors = Array.isArray(job.row_errors) ? job.row_errors.map((e) => String(e).replace(/^Sheet \d+ /, '')) : [];
        if (job.status === 'completed') return { ok: true, rowErrors };
        if (job.status === 'failed') {
            return { ok: false, rowErrors, message: String(job.error_message || '').replace(/^NOK:/, '').trim() || rowErrors[0] || 'The student upload failed.' };
        }
        if (job.status === 'processing' && job.updatedAt && Date.now() - new Date(job.updatedAt).getTime() > JOB_STALE_MS) {
            return { ok: false, rowErrors, message: 'The student upload was interrupted. Upload the same file again to continue — students already added are updated, not duplicated.' };
        }
    }
};

/** Students without an audience get the school's default, as Add a Class does. */
const defaultUserGroupFor = async (org_code) => {
    try {
        const groups = (await analyticsLicensesInfoService(org_code))?.data?.user_group_list;
        const picked = pickDefaultStudentUserGroup(groups);
        if (picked) return picked;
    } catch (e) { /* fall through */ }
    try {
        const first = (await getOrgModulesService({ org_code, page: 1, limit: 1 }))?.data?.modules?.[0];
        if (first?.user_group) return String(first.user_group).trim();
    } catch (e) { /* none */ }
    return '';
};

const btnPrimary = {
    textTransform: 'none', fontWeight: 700, fontSize: '14px', borderRadius: '50px',
    backgroundColor: ORANGE, color: '#fff', padding: '9px 24px',
    '&:hover': { backgroundColor: '#e55e00' },
    '&.Mui-disabled': { backgroundColor: '#F3F4F6', color: '#9CA3AF' },
};
const btnOutline = {
    textTransform: 'none', fontWeight: 700, fontSize: '14px', borderRadius: '50px',
    color: TEXT, border: `1px solid ${BORDER}`, padding: '9px 24px', backgroundColor: 'var(--brand-surface, #fff)',
};
const STATUS_COLOR = { done: GREEN, failed: RED, partial: '#B45309', skipped: MUTED, running: ORANGE, waiting: MUTED };
const STATUS_TEXT = { done: 'Done', failed: 'Failed', partial: 'Done with issues', skipped: 'Skipped', running: 'Working…', waiting: 'Waiting' };

/* ── the preview: the first rows of what was read, one tab per kind ── */
// The import template's own column names (Ian, 13 Oct 2026), and its values as
// typed: "Singapore (+65)", "Singapore", N/A — not the codes they are saved as.
const accountPreviewCols = (isPartner) => [
    [isPartner ? 'School or Network Name' : 'School Name', 'org_name'], ['Contact First Name', 'contact_first_name'],
    ['Contact Last Name', 'contact_last_name'], ['Contact Email', 'contact_email'], ['Country Code', '_country_code'],
    ['Phone Number', 'phone'], ['Address - Line 1', 'address1'], ['Country', '_country'],
    ['State / Province / Region', '_state'], ['City', 'city'], ['Postal Code', 'postal_code'],
];
const asTyped = (rows) => rows.map((r) => ({
    ...r,
    _country_code: r.country_code || r.dial_code,
    _country: r.country_typed || r.country,
    _state: r.state_typed !== undefined ? r.state_typed : r.state,
}));
const previewTabs = (parsed, isPartner) => {
    const students = parsed.classes.flatMap((c) => c.students.map((s) => ({ ...s, _class: c.class_name })));
    return [
        isPartner && { key: 'networks', label: 'Networks', rows: asTyped(parsed.networks), cols: accountPreviewCols(true) },
        { key: 'schools', label: 'Schools', rows: asTyped(parsed.schools), cols: isPartner ? [...accountPreviewCols(true), ['Parent Network Email', 'network_email']] : accountPreviewCols(false) },
        !isPartner && {
            key: 'classes', label: 'Classes', rows: parsed.classes.map((c) => ({ ...c, _students: c.students.length })),
            cols: [['Class Name', 'class_name'], ['Grade Level', 'grade_level'], ['Academic Year', 'academic_year'], ['Teacher Email', 'incharge_email'], ['School Email', 'school_email'], ['Students', '_students']],
        },
        !isPartner && {
            key: 'students', label: 'Students', rows: students,
            cols: [['Class', '_class'], ['First Name', 'first_name'], ['Last Name', 'last_name'], ['Birthdate', 'birthdate'], ['Learner Email', 'student_email'], ['Parent Email', 'email_address'], ['Gender', 'gender'], ['City', 'city'], ['Country', 'country'], ['State', 'state'], ['Postal Code', 'postal_code']],
        },
    ].filter(Boolean).filter((t) => t.rows.length);
};

const PreviewTable = ({ tab }) => (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'auto', maxHeight: 260 }}>
        <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
            <Box component="thead">
                <Box component="tr">
                    {tab.cols.map(([label]) => (
                        <Box component="th" key={label} sx={{ position: 'sticky', top: 0, textAlign: 'left', px: 1.25, py: 0.9, backgroundColor: '#F9FAFB', color: MUTED, fontWeight: 700, whiteSpace: 'nowrap', borderBottom: `1px solid ${BORDER}` }}>{label}</Box>
                    ))}
                </Box>
            </Box>
            <Box component="tbody">
                {tab.rows.slice(0, PREVIEW_ROWS).map((row, i) => (
                    <Box component="tr" key={i}>
                        {tab.cols.map(([label, key]) => (
                            <Box component="td" key={label} sx={{ px: 1.25, py: 0.75, color: TEXT, whiteSpace: 'nowrap', borderTop: i ? `1px solid ${BORDER}` : 'none' }}>{String(row[key] ?? '') || '—'}</Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const BulkUploadSchoolsDialog = ({ open, onClose, onReopen, kind, onChanged, onManual }) => {
    const isPartner = kind === UPLOAD_KIND_PARTNER;
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [reading, setReading] = useState(false);
    const [parsed, setParsed] = useState(null);
    const [existing, setExisting] = useState(null);
    const [previewKey, setPreviewKey] = useState(null);
    // The rows of a run live in a ref — thousands of them, updated constantly.
    // `run` is the small summary the screen draws from, refreshed a few times a second.
    const itemsRef = useRef([]);
    const [run, setRun] = useState(null); // { done, total, finished, detail, counts, tick }
    const running = !!run && !run.finished;
    // Classes are sent from this page once their schools are in, so a file with
    // classes needs the page; schools and networks alone run on the server.
    const [needsPage, setNeedsPage] = useState(false);

    // Leaving the page would stop the classes still to be sent.
    useEffect(() => {
        if (!running || !needsPage) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [running, needsPage]);

    // ── Background upload resume ── the same as the class page: the job runs on
    // the server, so on coming back to this page, ask for the workspace's
    // running schools / networks upload and re-attach the progress bar to it.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await clientBulkUploadStatusService();
                let job = res === false ? null : res?.data;
                if (cancelled || !job || job.status !== 'processing' || !job.job_id) return;
                // Skip stalled jobs — resuming one would just poll forever.
                if (job.updatedAt && Date.now() - new Date(job.updatedAt).getTime() > JOB_STALE_MS) return;
                if (itemsRef.current.length) return;
                const jobId = job.job_id;
                const show = (j, finished) => {
                    const total = Number(j.total_rows) || 0;
                    const done = finished ? total : Math.min(Number(j.processed_rows) || 0, total);
                    itemsRef.current = (j.results || []).map((r) => ({ type: 'school', label: r.label, status: r.status === 'skipped' ? 'skipped' : 'failed', message: r.message }));
                    if (finished && j.status !== 'completed') itemsRef.current.unshift({ type: 'school', label: 'Upload', status: 'failed', message: j.error_message || 'The upload was interrupted. Upload the same file again to continue.' });
                    const c = j.counts || {};
                    setRun({
                        done, total, finished, tick: Date.now(),
                        detail: finished ? '' : `${done.toLocaleString()} of ${total.toLocaleString()} schools and networks`,
                        counts: { done: c.done || 0, skipped: c.skipped || 0, failed: (c.failed || 0) + (finished && j.status !== 'completed' ? 1 : 0) },
                    });
                };
                setNeedsPage(false);
                show(job, false);
                let failedPolls = 0;
                for (;;) {
                    await sleep(BULK_POLL_MS);
                    if (cancelled) return;
                    try {
                        const st = await clientBulkUploadStatusService(jobId);
                        if (st !== false && st?.data) job = st.data;
                        failedPolls = 0;
                    } catch (e) {
                        failedPolls += 1;
                        if (failedPolls >= 4) { show({ ...job, status: 'failed', error_message: 'Lost track of the upload (connection). It continues on the server — reopen this page in a few minutes.' }, true); return; }
                        continue;
                    }
                    if (cancelled) return;
                    const stale = job.updatedAt && Date.now() - new Date(job.updatedAt).getTime() > JOB_STALE_MS;
                    if (job.status !== 'processing' || stale) {
                        show(stale && job.status === 'processing' ? { ...job, status: 'failed' } : job, true);
                        if (typeof onChanged === 'function') onChanged();
                        return;
                    }
                    show(job, false);
                }
            } catch (e) { /* silent — no bar is an acceptable fallback */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reset = () => {
        setFile(null); setParsed(null); setRun(null); setPreviewKey(null);
        itemsRef.current = [];
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleClose = () => {
        // Closing while it runs only hides the window; the upload carries on.
        if (!running) reset();
        onClose();
    };

    const downloadTemplate = async () => {
        // Ian's import template, as it is (13 Oct 2026).
        await downloadImportTemplate(schoolsImportTemplateKind(kind));
    };

    const readFile = async (f) => {
        if (!f) return;
        setFile(f); setParsed(null); setRun(null); setPreviewKey(null); setReading(true);
        // Let the spinner paint before a large file is read.
        await sleep(30);
        try {
            const have = describeExisting(await loadAllClients());
            setExisting(have);
            setParsed(await parseSchoolsUploadWorkbook(f, kind, have));
        } catch (e) {
            setParsed({ networks: [], schools: [], classes: [], errors: [], notes: [], fatal: e?.message || 'Could not read this file.' });
        }
        setReading(false);
    };

    const start = async () => {
        if (!parsed || parsed.fatal || parsed.errors.length || running) return;

        /* ── the rows of this run, in the order they are listed ── */
        const items = [];
        const add = (type, label) => { items.push({ type, label, status: 'waiting', message: '' }); return items.length - 1; };

        /*
         * Networks and schools go to the bulk upload endpoint as ONE request;
         * the server creates them in the background and reports each row back
         * by its key — n0 (network), n0s3 (a school inside it), s12 (a school).
         * The WHOLE file is sent, rows already in the workspace included — the
         * server skips those. That keeps the upload identical from one try to the
         * next, which is what lets the server resume an interrupted one.
         */
        const payload = { networks: [], schools: [] };
        const itemByKey = new Map();      // server key → its row in `items`
        const emailByKey = new Map();     // server key → that school's contact email
        const existingNetworks = new Map((existing?.networks || []).map((n) => [lower(n.email), n]));
        const sentNetworkEmails = new Set();

        parsed.networks.forEach((n) => {
            const item = add('network', `Network "${n.org_name}"`);
            const own = parsed.schools.filter((s) => lower(s.network_email) === lower(n.contact_email));
            const i = payload.networks.length;
            payload.networks.push(networkRowToPayload(n, own));
            sentNetworkEmails.add(lower(n.contact_email));
            itemByKey.set(`n${i}`, item);
            own.forEach((s, j) => {
                const key = `n${i}s${j}`;
                itemByKey.set(key, add('school', `School "${s.org_name}"`));
                emailByKey.set(key, lower(s.contact_email));
            });
        });
        parsed.schools.forEach((s) => {
            // Travelling with its network above.
            if (lower(s.network_email) && sentNetworkEmails.has(lower(s.network_email))) return;
            const item = add('school', `School "${s.org_name}"`);
            // A network already in the workspace that the school joins (partner file only).
            const network = lower(s.network_email) ? existingNetworks.get(lower(s.network_email)) : null;
            const key = `s${payload.schools.length}`;
            payload.schools.push({ ...schoolRowToPayload(s), ...(network ? { network_org_code: network.org_code } : {}) });
            itemByKey.set(key, item);
            emailByKey.set(key, lower(s.contact_email));
        });
        const classPlans = parsed.classes.map((c) => ({
            row: c,
            item: add('class', `Class "${c.class_name}"${c.students.length ? ` + ${plural(c.students.length, 'student')}` : ''} → ${c.school_email}`),
        }));

        itemsRef.current = items;
        setNeedsPage(classPlans.length > 0);
        const state = { done: 0, total: items.length, finished: false, detail: '' };
        let lastPaint = 0;
        const publish = (force = false) => {
            const now = Date.now();
            if (!force && now - lastPaint < 250) return;
            lastPaint = now;
            const counts = {};
            for (const it of items) counts[it.status] = (counts[it.status] || 0) + 1;
            setRun({ ...state, counts, tick: now });
        };
        const mark = (i, status, message = '') => {
            const was = items[i].status;
            items[i] = { ...items[i], status, message };
            if (status !== 'running' && status !== 'waiting' && (was === 'running' || was === 'waiting')) state.done += 1;
            publish();
        };
        publish(true);

        const failedSchoolEmails = new Set();
        let changed = false;

        /* ── 1 + 2. networks and schools: one server job, polled ── */
        const serverKeys = [...itemByKey.keys()];
        if (serverKeys.length) {
            const settled = new Set();
            const settle = (key, status, message = '') => {
                if (settled.has(key) || !itemByKey.has(key)) return;
                settled.add(key);
                // Its school did not make it — a class for it has nowhere to go.
                if (status !== 'done' && message !== 'Already in your workspace' && emailByKey.has(key)) failedSchoolEmails.add(emailByKey.get(key));
                mark(itemByKey.get(key), status, message);
            };
            const settleRest = (status, message = '') => serverKeys.forEach((k) => settle(k, status, message));
            serverKeys.forEach((k) => { items[itemByKey.get(k)] = { ...items[itemByKey.get(k)], status: 'running' }; });
            publish(true);
            try {
                const res = await startClientBulkUploadService(payload);
                const jobId = res?.data?.job_id;
                if (!jobId) throw new Error('The upload could not start.');
                let failedPolls = 0;
                for (;;) {
                    await sleep(BULK_POLL_MS);
                    let job = null;
                    try {
                        const st = await clientBulkUploadStatusService(jobId);
                        job = st === false ? null : st?.data;
                        failedPolls = 0;
                    } catch (e) {
                        failedPolls += 1;
                        if (failedPolls >= 4) { settleRest('failed', 'Lost track of the upload (connection). It continues on the server — reopen this page in a few minutes.'); break; }
                        continue;
                    }
                    if (!job) continue;
                    (job.results || []).forEach((r) => settle(r.key, r.status === 'skipped' ? 'skipped' : 'failed', r.message));
                    const processed = Number(job.processed_rows) || 0;
                    state.detail = `${processed.toLocaleString()} of ${(Number(job.total_rows) || serverKeys.length).toLocaleString()} schools and networks`;
                    // Rows the server has finished and did not report are created.
                    state.done = Math.min(processed, serverKeys.length);
                    publish();
                    if (job.status === 'completed') { changed = true; settleRest('done'); break; }
                    if (job.status === 'failed') { changed = true; settleRest('failed', job.error_message || 'The upload failed.'); break; }
                    if (job.updatedAt && Date.now() - new Date(job.updatedAt).getTime() > JOB_STALE_MS) {
                        settleRest('failed', 'The upload was interrupted. The rows marked Done are in your workspace — remove them from the file and upload the rest again.');
                        break;
                    }
                }
            } catch (e) {
                settleRest('failed', errText(e, 'The upload could not start.'));
            }
            state.detail = '';
            state.done = items.filter((it) => it.status !== 'waiting' && it.status !== 'running').length;
        }
        publish(true);

        /* ── 3. classes: resolved to their school, then sent in bulk ── */
        if (classPlans.length) {
            let schoolsByEmail = new Map();
            try {
                schoolsByEmail = new Map(describeExisting(await loadAllClients()).schools.map((s) => [lower(s.email), s]));
            } catch (e) { /* every class below reports its school as not found */ }

            const emptyBySchool = new Map();   // school key → { school, plans }
            const studentsByOrg = new Map();   // org_code   → Map(school key → { school, plans })
            classPlans.forEach((plan) => {
                const c = plan.row;
                if (failedSchoolEmails.has(lower(c.school_email))) { mark(plan.item, 'skipped', 'Its school could not be created'); return; }
                const school = schoolsByEmail.get(lower(c.school_email));
                if (!school?.org_code) { mark(plan.item, 'failed', `No school with the contact email ${c.school_email}.`); return; }
                const key = `${school.org_code}::${school.institute_code || ''}`;
                if (!c.students.length) {
                    if (!emptyBySchool.has(key)) emptyBySchool.set(key, { school, plans: [] });
                    emptyBySchool.get(key).plans.push(plan);
                } else {
                    if (!studentsByOrg.has(school.org_code)) studentsByOrg.set(school.org_code, new Map());
                    const bySchool = studentsByOrg.get(school.org_code);
                    if (!bySchool.has(key)) bySchool.set(key, { school, plans: [] });
                    bySchool.get(key).plans.push(plan);
                }
            });

            // Empty classes — per school, CLASS_BATCH at a time.
            for (const { school, plans } of emptyBySchool.values()) {
                for (let i = 0; i < plans.length; i += CLASS_BATCH) {
                    const batch = plans.slice(i, i + CLASS_BATCH);
                    batch.forEach((p) => mark(p.item, 'running'));
                    try {
                        const res = await createOrgClassSectionsService(
                            school.org_code, school.org_name, batch.map((p) => classToShellPayload(p.row)),
                            school.institute_code ? { institute_code: school.institute_code } : null,
                        );
                        if (!(res?.data?.success || Array.isArray(res?.data?.new_list))) throw new Error('Could not create these classes.');
                        changed = true;
                        batch.forEach((p) => mark(p.item, 'done'));
                    } catch (e) {
                        batch.forEach((p) => mark(p.item, 'failed', errText(e, 'Could not create this class.')));
                    }
                }
            }

            // Students — ONE background job per workspace, one block per school.
            // The server works through it 100 rows at a time; this only polls.
            for (const [org_code, bySchool] of studentsByOrg.entries()) {
                const plans = [...bySchool.values()].flatMap((g) => g.plans);
                plans.forEach((p) => mark(p.item, 'running'));
                try {
                    const userGroup = await defaultUserGroupFor(org_code);
                    const data = [...bySchool.values()].map(({ school, plans: schoolPlansOfBlock }) => ({
                        head: [school.institute_code ? { institute_code: school.institute_code } : {}],
                        license: schoolPlansOfBlock.flatMap((p) => p.row.students.map((s) => (
                            s.user_group || !userGroup ? s : { ...s, user_group: userGroup }
                        ))),
                    }));
                    const res = await analyticsPostUsersCsvAsyncService({ org_code, org_type: ORG_TYPE_SCHOOL, data, options: STUDENT_UPLOAD_OPTIONS });
                    const jobId = res?.data?.job_id;
                    if (!jobId) throw new Error('The student upload could not start.');
                    const result = await waitForStudentJob(org_code, jobId, (done, total) => {
                        state.detail = total ? `${done.toLocaleString()} of ${total.toLocaleString()} students` : '';
                        publish();
                    });
                    state.detail = '';
                    changed = true;
                    if (!result.ok) {
                        plans.forEach((p) => mark(p.item, 'failed', result.message));
                    } else {
                        plans.forEach((p) => mark(p.item, 'done'));
                    }
                    // Rows the server refused are listed on their own — the rest were added.
                    if (result.rowErrors.length) {
                        result.rowErrors.forEach((msg) => items.push({ type: 'student', label: 'Student row', status: 'failed', message: msg }));
                        state.total = items.length;
                        state.done += result.rowErrors.length;
                    }
                } catch (e) {
                    state.detail = '';
                    plans.forEach((p) => mark(p.item, 'failed', errText(e, 'The student upload failed.')));
                }
            }
        }

        state.finished = true;
        state.done = state.total;
        publish(true);
        if (changed && typeof onChanged === 'function') onChanged();
    };

    const counts = run?.counts || {};
    const canStart = !!parsed && !parsed.fatal && parsed.errors.length === 0 && !running && !run;
    const tabs = parsed && !parsed.fatal ? previewTabs(parsed, isPartner) : [];
    const activeTab = tabs.find((t) => t.key === previewKey) || tabs[0] || null;
    // What the result list draws: anything that needs a look first, then what is
    // in flight, then the most recent successes — never the whole run.
    const items = itemsRef.current;
    const shown = run ? [
        ...items.filter((i) => i.status === 'failed' || i.status === 'partial'),
        ...items.filter((i) => i.status === 'running'),
        ...items.filter((i) => i.status === 'skipped'),
        ...items.filter((i) => i.status === 'done').slice(-20),
        ...(run.finished ? [] : items.filter((i) => i.status === 'waiting').slice(0, 5)),
    ].slice(0, LIST_CAP) : [];

    return (
        <>
            {/* The upload keeps going with the window closed — this bar shows it and reopens it. */}
            {!open && run && (
                <Box
                    onClick={() => typeof onReopen === 'function' && onReopen()}
                    sx={{
                        cursor: 'pointer', position: 'fixed', right: 24, bottom: 24, zIndex: 1300, width: 320, p: 2,
                        backgroundColor: 'var(--brand-surface, #fff)', border: `1px solid ${BORDER}`, borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }}
                >
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: TEXT, mb: 0.75 }}>
                        {run.finished ? 'Upload finished' : `Uploading… ${run.done.toLocaleString()} of ${run.total.toLocaleString()}`}
                    </Typography>
                    <LinearProgress variant="determinate" value={run.total ? (run.done / run.total) * 100 : 0} sx={{ height: 6, borderRadius: 3 }}/>
                    <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
                        {run.finished ? 'Click to see the result.' : (run.detail || (needsPage ? 'Keep this page open. Click for details.' : 'Click for details.'))}
                    </Typography>
                </Box>
            )}

            {/*
              * Reached from inside the Add dialog, so it opens at that dialog's width
              * and frame — the switch between the two no longer jumps in size. It
              * widens only once a file is read, when the preview table needs the room.
              */}
            <Dialog open={open} onClose={handleClose} maxWidth={(parsed || run) ? 'md' : 'sm'} fullWidth PaperProps={{ sx: { border: '2px solid #000000', borderRadius: '15px', backgroundColor: 'var(--brand-bg, #FFFBF7)' } }}>
                <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
                    {/*
                      * The import is the other half of "Add a School (or
                      * Network" — the same heading and the same switch, so it can go
                      * back to Add Manually instead of being a separate pop-up
                      * (LA-PW-07-Oct-03, slide 12; Ian-SNW-07Oct2026-1, slide 49).
                      */}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ position: 'relative', mb: 1.5 }}>
                            <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: TEXT, textAlign: 'center' }}>
                                {isPartner ? 'Add a School or Network' : 'Add a School'}
                            </Typography>
                            <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', right: 0, top: -4 }}><CloseIcon fontSize="small"/></IconButton>
                        </Box>
                        {/* Mid-upload and on the result there is nothing to switch away from. */}
                        {!run && onManual && (
                            <EntryModeToggle
                                centered active="upload" manualLabel="Add Manually" uploadLabel="Import CSV"
                                onManual={() => { if (!running) { reset(); onManual(); } }}
                            />
                        )}
                        <Typography sx={{ fontSize: 14, color: TEXT, textAlign: 'center' }}>
                            Import multiple {isPartner ? 'networks and schools' : 'schools'} at once using the CSV template.
                        </Typography>
                    </Box>

                    {/* ── choose a file ── */}
                    {!run && (
                        <Box sx={{ border: `2px dashed ${BORDER}`, borderRadius: '14px', p: 3, textAlign: 'center', mb: 2.5, backgroundColor: 'var(--brand-surface, #fff)' }}>
                            <Typography sx={{ fontSize: 14, color: TEXT, fontWeight: 700, mb: 0.5 }}>
                                {file ? `Selected: ${file.name}` : 'Set up your CSV file'}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 2 }}>
                                {`Follow the instructions in the template to add your ${isPartner ? 'networks and schools' : 'schools'}. Imported entries are saved as drafts, and their workspaces aren’t activated yet.`}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                {/* Download Template first: it is the first step (LA-PW-07-Oct-03). */}
                                <Button sx={btnOutline} onClick={downloadTemplate} disabled={reading}>Download Template</Button>
                                <Button sx={btnPrimary} onClick={() => fileRef.current?.click()} disabled={reading}>
                                    {file ? 'Upload Another File' : 'Upload File'}
                                </Button>
                                <input
                                    ref={fileRef} type="file" style={{ display: 'none' }}
                                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                    onChange={(e) => readFile(e.target.files?.[0])}
                                />
                            </Box>
                        </Box>
                    )}

                    {reading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1 }}>
                            <CircularProgress sx={{ color: ORANGE }}/>
                            <Typography sx={{ fontSize: 12.5, color: MUTED }}>Reading and checking the file…</Typography>
                        </Box>
                    )}

                    {/* ── what the file holds, its preview, and its problems ── */}
                    {!run && parsed && !reading && (
                        <Box>
                            {parsed.fatal ? (
                                <Typography sx={{ fontSize: 14, color: RED, fontWeight: 600 }}>{parsed.fatal}</Typography>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: 14, color: TEXT, fontWeight: 700, mb: 1 }}>
                                        Found: {[
                                            isPartner ? plural(parsed.networks.length, 'network') : null,
                                            plural(parsed.schools.length, 'school'),
                                            isPartner ? null : `${plural(parsed.classes.length, 'class')} (${plural(parsed.classes.reduce((n, c) => n + c.students.length, 0), 'student')})`,
                                        ].filter(Boolean).join(' · ')}
                                    </Typography>
                                    {parsed.notes.map((n, i) => (
                                        <Typography key={i} sx={{ fontSize: 12.5, color: MUTED, mb: 0.5 }}>• {n}</Typography>
                                    ))}

                                    {parsed.errors.length > 0 && (
                                        <Box sx={{ mt: 1.5, mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                                                <Typography sx={{ fontSize: 14, color: RED, fontWeight: 700 }}>
                                                    {plural(parsed.errors.length, 'problem')} to fix in the file — nothing has been added
                                                </Typography>
                                            </Box>
                                            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', maxHeight: 240, overflowY: 'auto', backgroundColor: 'var(--brand-surface, #fff)' }}>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '130px 90px 190px 1fr', px: 1.5, py: 1, backgroundColor: '#F9FAFB', fontSize: 12, fontWeight: 700, color: MUTED }}>
                                                    <span>Sheet</span><span>Row</span><span>Column</span><span>Problem</span>
                                                </Box>
                                                {parsed.errors.slice(0, LIST_CAP).map((e, i) => (
                                                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '130px 90px 190px 1fr', px: 1.5, py: 0.9, borderTop: `1px solid ${BORDER}`, fontSize: 12.5, color: TEXT }}>
                                                        <span>{e.sheet}</span><span>{e.row}</span><span>{e.column}</span><span>{e.message}</span>
                                                    </Box>
                                                ))}
                                            </Box>
                                            {parsed.errors.length > LIST_CAP && (
                                                <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
                                                    Showing the first {LIST_CAP} of {parsed.errors.length.toLocaleString()}.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {activeTab && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT, mr: 0.5 }}>Preview</Typography>
                                                {tabs.map((t) => (
                                                    <Box
                                                        key={t.key}
                                                        onClick={() => setPreviewKey(t.key)}
                                                        sx={{
                                                            cursor: 'pointer', fontSize: 12.5, fontWeight: 700, px: 1.5, py: 0.5, borderRadius: '50px',
                                                            border: `1px solid ${activeTab.key === t.key ? ORANGE : BORDER}`,
                                                            color: activeTab.key === t.key ? ORANGE : MUTED,
                                                            backgroundColor: activeTab.key === t.key ? 'rgba(255,105,0,0.08)' : 'transparent',
                                                        }}
                                                    >
                                                        {t.label}&nbsp;&nbsp;{t.rows.length.toLocaleString()}
                                                    </Box>
                                                ))}
                                            </Box>
                                            <PreviewTable tab={activeTab}/>
                                            {activeTab.rows.length > PREVIEW_ROWS && (
                                                <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
                                                    Showing the first {PREVIEW_ROWS} of {activeTab.rows.length.toLocaleString()} — every row is uploaded.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {/* ── progress and result ── */}
                    {run && (
                        <Box>
                            <Typography sx={{ fontSize: 14, color: TEXT, fontWeight: 700, mb: 1 }}>
                                {run.finished
                                    ? `Finished — ${[
                                        counts.done ? `${counts.done.toLocaleString()} added` : null,
                                        counts.partial ? `${counts.partial.toLocaleString()} added with issues` : null,
                                        counts.skipped ? `${counts.skipped.toLocaleString()} skipped` : null,
                                        counts.failed ? `${counts.failed.toLocaleString()} failed` : null,
                                    ].filter(Boolean).join(', ') || 'nothing to do'}`
                                    : `Uploading… ${run.done.toLocaleString()} of ${run.total.toLocaleString()}${run.detail ? ` (${run.detail})` : ''}`}
                            </Typography>
                            <LinearProgress variant="determinate" value={run.total ? (run.done / run.total) * 100 : 100} sx={{ height: 8, borderRadius: 4, mb: 1.5 }}/>
                            {!run.finished && (
                                <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 1.5 }}>
                                    You can close this window and keep working — the upload continues.{needsPage ? ' Keep this page open until it finishes.' : ''}
                                </Typography>
                            )}
                            <Box sx={{ display: shown.length ? 'block' : 'none', border: `1px solid ${BORDER}`, borderRadius: '10px', maxHeight: 300, overflowY: 'auto', backgroundColor: 'var(--brand-surface, #fff)' }}>
                                {shown.map((it, i) => (
                                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 1, px: 1.5, py: 0.9, borderTop: i ? `1px solid ${BORDER}` : 'none', fontSize: 12.5 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 12.5, color: TEXT }}>{it.label}</Typography>
                                            {it.message && <Typography sx={{ fontSize: 12, color: STATUS_COLOR[it.status] }}>{it.message}</Typography>}
                                        </Box>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: STATUS_COLOR[it.status], textAlign: 'right' }}>
                                            {STATUS_TEXT[it.status]}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 0.75 }}>
                                <Typography sx={{ fontSize: 12, color: MUTED }}>
                                    {items.length > shown.length ? `Showing ${shown.length} of ${items.length.toLocaleString()} rows — anything that failed is listed first.` : ''}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                        <Button sx={btnOutline} onClick={handleClose}>{running ? 'Hide' : (!run ? 'Cancel' : 'Close')}</Button>
                        {!run && <Button sx={btnPrimary} onClick={start} disabled={!canStart}>Import</Button>}
                        {run?.finished && <Button sx={btnPrimary} onClick={reset}>Upload Another File</Button>}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default BulkUploadSchoolsDialog;
